import { Component, Input, OnDestroy, inject, ChangeDetectionStrategy, signal } from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { LlmService } from '../../shared/services/llm/llm.service';
import {
  LlmStreamEvent,
  LlmUsage,
} from '../../shared/interfaces/llm.interface';
import { ExecutionPlanService } from '../../shared/services/execution-plan/execution-plan.service';
import { MarkdownService } from '../../shared/services/markdown/markdown.service';
import { RoutineHistoryItem } from '../../shared/interfaces/execution-plan.interfaces';

@Component({
  selector: 'app-job-pattern-analysis',
  templateUrl: './job-pattern-analysis.component.html',
  styleUrls: ['./job-pattern-analysis.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
})
export class JobPatternAnalysisComponent implements OnDestroy {
  readonly #llm = inject(LlmService);
  readonly #execPlan = inject(ExecutionPlanService);
  readonly #markdown = inject(MarkdownService);
  private readonly destroy$ = new Subject<void>();

  @Input() projectId!: number;
  @Input() planId!: number;
  @Input() routineName!: string;

  analysisText = signal('');
  renderedAnalysisHtml = signal('');
  model = signal<string | null>(null);
  usage = signal<LlmUsage | null>(null);
  streaming = signal(false);
  loadingJobs = signal(false);
  error = signal<string | null>(null);
  collapsed = signal(false);
  copied = signal(false);

  private abortFn: (() => void) | null = null;
  /** Coalesces markdown parse to one rAF per frame while streaming (avoids O(n²) full re-parses per token). */
  private analysisHtmlRaf = 0;

  get loading(): boolean {
    return this.loadingJobs() || this.streaming();
  }

  get hasAnalysis(): boolean {
    return this.analysisText().length > 0;
  }

  ngOnDestroy(): void {
    if (this.analysisHtmlRaf !== 0) {
      cancelAnimationFrame(this.analysisHtmlRaf);
      this.analysisHtmlRaf = 0;
    }
    this.abortFn?.();
    this.destroy$.next();
    this.destroy$.complete();
  }

  analyze(): void {
    this.loadingJobs.set(true);
    this.error.set(null);
    this.analysisText.set('');
    this.renderedAnalysisHtml.set('');
    this.model.set(null);
    this.usage.set(null);

    this.#execPlan
      .getPlanHistory(this.projectId, this.planId, 20)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (history) => {
          this.loadingJobs.set(false);
          if (!history || history.length === 0) {
            this.error.set('No job history available to analyze');
            return;
          }
          this.#streamAnalysis(history);
        },
        error: (err) => {
          this.loadingJobs.set(false);
          this.error.set(
            err?.error?.message || err?.message || 'Failed to load job history',
          );
        },
      });
  }

  stopStreaming(): void {
    this.abortFn?.();
    this.abortFn = null;
    this.streaming.set(false);
    this.#flushAnalysisHtmlRender();
  }

  toggleCollapsed(): void {
    this.collapsed.update((v) => !v);
  }

  copyAnalysis(): void {
    if (!this.analysisText()) return;
    navigator.clipboard.writeText(this.analysisText()).then(() => {
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    });
  }

  #streamAnalysis(history: RoutineHistoryItem[]): void {
    this.streaming.set(true);

    const recentJobs = history.map((h) => ({
      job_id: h.jobId,
      status: h.status,
      scheduled_time: h.scheduledTime,
      started_at: h.startedAt ?? null,
      finished_at: h.finishedAt ?? null,
      error_details: h.errorDetails ?? null,
    }));

    const context: Record<string, unknown> = {
      routine_name: this.routineName,
      recent_jobs: recentJobs,
    };

    const { events$, abort } = this.#llm.streamOperationalChat([], {
      promptName: 'job_pattern_detection',
      context,
    });
    this.abortFn = abort;

    events$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (event: LlmStreamEvent) => {
        if ('error' in event) {
          this.error.set(event.error);
          this.streaming.set(false);
          this.#flushAnalysisHtmlRender();
          return;
        }
        if (event.content) {
          this.analysisText.update((v) => v + event.content);
          this.#scheduleAnalysisHtmlRender();
        }
        if (event.done && 'model' in event) {
          this.model.set(event.model);
          this.usage.set(event.usage);
          this.streaming.set(false);
          this.abortFn = null;
          this.#flushAnalysisHtmlRender();
        }
      },
      error: (err) => {
        this.error.set(err?.message || 'Streaming failed');
        this.streaming.set(false);
        this.abortFn = null;
        this.#flushAnalysisHtmlRender();
      },
      complete: () => {
        this.streaming.set(false);
        this.abortFn = null;
        this.#flushAnalysisHtmlRender();
      },
    });
  }

  #scheduleAnalysisHtmlRender(): void {
    if (this.analysisHtmlRaf !== 0) {
      return;
    }
    this.analysisHtmlRaf = requestAnimationFrame(() => {
      this.analysisHtmlRaf = 0;
      this.renderedAnalysisHtml.set(this.#markdown.parseSimpleMarkdownToHtml(
        this.analysisText(),
      ));
    });
  }

  #flushAnalysisHtmlRender(): void {
    if (this.analysisHtmlRaf !== 0) {
      cancelAnimationFrame(this.analysisHtmlRaf);
      this.analysisHtmlRaf = 0;
    }
    this.renderedAnalysisHtml.set(this.#markdown.parseSimpleMarkdownToHtml(
      this.analysisText(),
    ));
  }
}
